import { loadUserData, whenAuthReady } from './db.js';
import Chart from 'chart.js/auto';
import { durationSecOf } from './model.js';

// DURATION HISTOGRAM

// Minutes for a completed record (exact seconds when available, legacy "(h:mm)" otherwise)
function durationMinutes(c) {
  const sec = durationSecOf(c);
  return sec === null ? null : sec / 60;
}

// Main stats loader function
async function loadCompletedDurations() {
  let completed = [];

  // Try Firestore first (if logged in)
  let dbData = null;
  try {
    await whenAuthReady();
    dbData = await loadUserData({ all: true });
    completed = dbData?.completed || [];
  } catch (e) {
    completed = [];
  }

  // If there's no Firestore data (anonymous or offline), fall back to localStorage
  if (!completed.length) {
    try {
      completed = JSON.parse(localStorage.getItem('fishbowlDone') || '[]');
    } catch (e) {
      completed = [];
    }
  }

  // Extract durations in minutes
  const durations = completed
    .map(durationMinutes)
    .filter(d => typeof d === 'number' && !isNaN(d) && d >= 0);

  return durations;
}

function binDurations(durations, bins) {
  // bins: array of bin edges, e.g. [0, 5, 10, 20, 30, 45, 60, 120]
  const counts = new Array(bins.length - 1).fill(0);
  for (const duration of durations) {
    let idx = bins.length - 2;
    for (let i = 0; i < bins.length - 1; i++) {
      if (duration >= bins[i] && duration < bins[i+1]) {
        idx = i;
        break;
      }
    }
    counts[idx]++;
  }
  return counts;
}

function renderHistogram(durations) {
  // Define bins (match what your users need!)
  const bins = [0, 5, 10, 20, 30, 45, 60, 90, 120];
  const labels = bins.slice(0, -1).map((b, i) =>
    i === bins.length-2 ? `≥${bins[i]} min` : (i === 0 ? `< ${bins[1]} min` : `${bins[i]}–${bins[i+1]} min`)
  );
  const counts = binDurations(durations, bins);

  // Draw the chart
  const ctx = document.getElementById('histogramChart').getContext('2d');
  if (!ctx) return;

  const dark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const textColor = dark ? '#ddd' : '#333';
  const gridColor = dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  new Chart(ctx, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: "Number of Tasks",
        data: counts,
        backgroundColor: "rgba(0, 119, 204, 0.7)",
        borderColor: "rgba(0, 119, 204, 1)",
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1, precision: 0, color: textColor },
          grid: { color: gridColor },
          title: { display: true, text: "Number of Tasks", color: textColor }
        },
        x: {
          ticks: { color: textColor },
          grid: { color: gridColor },
          title: { display: true, text: "Task Duration", color: textColor }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => `${ctx.parsed.y} tasks`
          }
        }
      }
    }
  });
}

// Statistics are a sign-up incentive: anonymous visitors see the gate instead.
async function passesGate() {
  const user = await whenAuthReady();
  const allowed = !!(user && !user.isAnonymous);
  document.getElementById('statsGate').hidden = allowed;
  document.getElementById('statsBody').hidden = !allowed;
  return allowed;
}
const gate = passesGate();

// Main entrypoint
window.addEventListener('DOMContentLoaded', async () => {
  if (!(await gate)) return;
  const durations = await loadCompletedDurations();
  renderHistogram(durations);

  // Optionally show info if no data
  if (!durations.length) {
    const chartContainer = document.getElementById('histogramChart').parentElement;
    const msg = document.createElement('div');
    msg.textContent = "No completed tasks with recorded durations yet!";
    msg.style.textAlign = "center";
    msg.style.color = "#666";
    msg.style.margin = "1rem 0";
    chartContainer.appendChild(msg);
  }
});




// --- Full Functional Heatmap Code with Enhanced completedOn Handling ---

function getWeekHourIndex(date) {
  if (!(date instanceof Date) || isNaN(date)) {
    console.warn('Invalid date in getWeekHourIndex:', date);
    return -1;
  }
  let day = date.getDay();
  day = day === 0 ? 6 : day - 1;
  if (day < 0 || day > 6) {
    console.warn('Day out of range in getWeekHourIndex:', day, date);
    return -1;
  }
  const hour = date.getHours();
  if (hour < 0 || hour > 23) {
    console.warn('Hour out of range in getWeekHourIndex:', hour, date);
    return -1;
  }
  return day * 24 + hour;
}

function intensityToColor(intensity) {
  const clamped = Math.min(intensity, 1);
  const light = { r: 224, g: 243, b: 219 };
  const dark = { r: 8, g: 104, b: 172 };
  function lerp(a, b, t) { return Math.round(a + (b - a) * t); }
  const r = lerp(light.r, dark.r, clamped);
  const g = lerp(light.g, dark.g, clamped);
  const b = lerp(light.b, dark.b, clamped);
  return `rgb(${r},${g},${b})`;
}

function renderHeatmapGrid(normalizedData, maxValue) {
  for (let i = 0; i < 168; i++) {
    const cell = document.getElementById(`cell-${i}`);
    if (!cell) continue;

    const intensity = normalizedData[i] || 0;

    if (intensity > 0) cell.style.backgroundColor = intensityToColor(intensity); else cell.style.backgroundColor = '';
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    cell.title = `${dayNames[Math.floor(i / 24)]} ${String(i % 24).padStart(2, '0')}:00\n` +
                 `Worked: ${Math.round(intensity * maxValue * 10) / 10} hours`;
  }
}


function createHeatmapGridWithHoursBelow() {
  const container = document.getElementById('heatmapGrid');
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  container.innerHTML = '';
  for (let day = 0; day < 7; day++) {
    const dayLabel = document.createElement('div');
    dayLabel.className = 'heatmap-day-label';
    dayLabel.textContent = dayNames[day];
    container.appendChild(dayLabel);
    for (let hour = 0; hour < 24; hour++) {
      const cell = document.createElement('div');
      cell.className = 'heatmap-cell';
      const cellIndex = day * 24 + hour;
      cell.id = `cell-${cellIndex}`;
      container.appendChild(cell);
    }
  }
  const emptyCellBelow = document.createElement('div');
  emptyCellBelow.style.gridColumn = '1';
  container.appendChild(emptyCellBelow);
  for (let hour = 0; hour < 24; hour++) {
    const hourLabel = document.createElement('div');
    hourLabel.className = 'heatmap-hour-label';
    hourLabel.textContent = hour;
    container.appendChild(hourLabel);
  }
}


function addDurationToWeekHours(startDate, durationMins, weekHours) {
  if (!(startDate instanceof Date) || isNaN(startDate) || durationMins <= 0) return;
  let startTime = startDate.getTime();
  const endTime = startTime + durationMins * 60 * 1000;
  while (startTime < endTime) {
    const currentDate = new Date(startTime);
    const idx = getWeekHourIndex(currentDate);
    if (idx < 0 || idx >= weekHours.length) break;
    const nextHour = new Date(currentDate);
    nextHour.setMinutes(60, 0, 0);
    const hourEndTime = nextHour.getTime();
    const overlapMs = Math.min(endTime, hourEndTime) - startTime;
    const overlapMinutes = overlapMs / (60 * 1000);
    weekHours[idx] += overlapMinutes / 60;
    startTime += overlapMs;
  }
}

// Enhanced conversion from completedOn to Date supporting multiple formats:
function convertToDate(completedOn) {
  if (!completedOn) return null;

  // Firestore Timestamp instance with toDate()
  if (typeof completedOn.toDate === 'function') {
    return completedOn.toDate();
  }

  // Plain object representing Firestore Timestamp with seconds and nanoseconds
  if (
    typeof completedOn === 'object' &&
    completedOn !== null &&
    typeof completedOn.seconds === 'number' &&
    typeof completedOn.nanoseconds === 'number'
  ) {
    const ms = completedOn.seconds * 1000 + Math.floor(completedOn.nanoseconds / 1e6);
    return new Date(ms);
  }

  // Number timestamp (milliseconds or seconds)
  if (typeof completedOn === 'number') {
    if (completedOn > 1e12) return new Date(completedOn);
    return new Date(completedOn * 1000);
  }

  // String or Date convertible
  const date = new Date(completedOn);
  if (date instanceof Date && !isNaN(date)) {
    return date;
  }

  // Unparseable
  return null;
}

// Normalize array so the max value is 1 (or 0 if all zero)
function normalizeArray(arr) {
  const max = Math.max(...arr);
  if (max <= 0) return arr.slice(); // avoid divide by zero
  return arr.map(v => v / max);
}


async function fetchCompletedTasksAndRenderHeatmap() {
  const weekHours = new Array(7 * 24).fill(0);
  let completed = [];

  try {
    await whenAuthReady();
    const dbData = await loadUserData({ all: true });
    completed = dbData?.completed || [];
  } catch (e) {
    console.error('Error loading from Firestore:', e);
    completed = [];
  }

  if (!completed.length) {
    try {
      completed = JSON.parse(localStorage.getItem('fishbowlDone') || '[]');
    } catch (e) {
      console.error('Error loading from localStorage:', e);
      completed = [];
    }
  }

  console.log(`Total tasks loaded: ${completed.length}`);

  let successfulEntries = 0;
  let rejectedEntries = 0;

  for (const task of completed) {
    if (!task.duration) {
      rejectedEntries++;
      console.warn('Rejected task due to missing duration:', task);
      continue;
    }
    if (!task.completedOn) {
      rejectedEntries++;
      console.warn('Rejected task due to missing completedOn:', task);
      continue;
    }

    const durationMins = durationMinutes(task);
    if (!durationMins) {
      rejectedEntries++;
      console.warn('Rejected task due to invalid duration:', task.duration, task);
      continue;
    }

    const completedDate = convertToDate(task.completedOn);

    if (!completedDate) {
      rejectedEntries++;
      console.warn('Rejected task due to unparseable completedOn:', task.completedOn, task);
      continue;
    }

    const startDate = new Date(completedDate.getTime() - durationMins * 60 * 1000);
    addDurationToWeekHours(startDate, durationMins, weekHours);

    successfulEntries++;
    console.log('Processed task:', {
      duration: task.duration,
      completedOn: completedDate.toISOString(),
      startDate: startDate.toISOString()
    });
  }

  console.log(`Successfully processed ${successfulEntries} tasks.`);
  console.log(`Rejected ${rejectedEntries} tasks.`);
  
  const maxValue = Math.max(...weekHours);
  const normalizedWeekHours = normalizeArray(weekHours);
  renderHeatmapGrid(normalizedWeekHours, maxValue);

}


// Initialize on DOM ready: build grid and load/render heatmap data
window.addEventListener('DOMContentLoaded', async () => {
  if (!(await gate)) return;
  createHeatmapGridWithHoursBelow();
  fetchCompletedTasksAndRenderHeatmap();
});
